import { createAsyncThunk } from '@reduxjs/toolkit'
import type { MembershipKpiItem } from '@constants/Membership/membershipPage.constants'
import {
  MembershipMemberApiPaymentCycle,
  MembershipMemberApiStatus,
  MembershipPageMember,
  MembershipPerk,
  MembershipViewSource,
  MembershipTier,
} from '@dataTypes/membership.type'
import {
  createMembershipMember as createMembershipMemberApi,
  createMembershipTier as createMembershipTierApi,
  deleteMembershipTier as deleteMembershipTierApi,
  getAllMembershipTiers as getAllMembershipTiersApi,
  getMembershipMemberById as getMembershipMemberByIdApi,
  getMembershipMemberKpis as getMembershipMemberKpisApi,
  getMembershipMembers as getMembershipMembersApi,
  MembershipMemberListResponse,
  MembershipMemberQueryFilters,
  MembershipTierListResponse,
  MembershipTierQueryFilters,
  updateMembershipMember as updateMembershipMemberApi,
  updateMembershipMemberStatus as updateMembershipMemberStatusApi,
  updateMembershipTier as updateMembershipTierApi,
  updateMembershipTierStatus as updateMembershipTierStatusApi,
} from '@services/api/billing/membership.api'
import {
  mapMembershipMemberToCreateRequest,
  mapMembershipMemberToUpdateRequest,
  mapMembershipTierToCreateRequest,
  mapMembershipTierToUpdateRequest,
} from '@services/api/billing/membership.mapper'
import { RootState } from '@store/index'

export type CreateMembershipMemberInput = {
  patientId: string
  membershipTierId: string
  paymentCycle: MembershipMemberApiPaymentCycle
  memberSince: string
  expiryDate: string
  recurringAmount: number
  autoPayMethod?: string
  notificationChannels?: string
}

export type UpdateMembershipMemberInput = {
  membershipMemberId: string
  membershipTierId: string
  paymentCycle: MembershipMemberApiPaymentCycle
  expiryDate: string
  recurringAmount: number
  autoPayMethod?: string
  notificationChannels?: string
}

export type SaveMembershipTierPayload = {
  tier: MembershipTier
  perks: MembershipPerk[]
}

const resolveClinicId = (state: RootState): string =>
  state.clinic.selectedClinic?.clinic_id ||
  localStorage.getItem('clinic_id') ||
  state.clinic.data[0]?.clinic_id ||
  ''

/** Surfaces the backend's own message instead of "Request failed with status code 400". */
const toErrorMessage = (error: unknown, fallback: string): string => {
  const response = (
    error as { response?: { data?: { message?: string; error?: unknown } } }
  )?.response
  const data = response?.data

  if (data && Array.isArray(data.error) && data.error.length > 0) {
    const details = data.error
      .map((detail: { path?: string; msg?: string }) =>
        [detail.path, detail.msg].filter(Boolean).join(': '),
      )
      .filter(Boolean)
      .join(', ')
    if (details) return `${data.message ?? fallback} (${details})`
  }

  if (data?.message) return data.message
  return error instanceof Error ? error.message : fallback
}

export const getAllMembershipTiers = createAsyncThunk<
  MembershipTierListResponse,
  MembershipTierQueryFilters | void,
  { rejectValue: string; state: RootState }
>(
  'api/membership-tier/get/all',
  async (queryFilters, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const clinicId = resolveClinicId(state)

      const filters: MembershipTierQueryFilters = {
        ...(queryFilters || {}),
      }

      if (clinicId) {
        filters.clinic_id = clinicId
      }

      return await getAllMembershipTiersApi(
        Object.keys(filters).length > 0 ? filters : undefined,
      )
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, 'Failed to fetch membership tiers'),
      )
    }
  },
)

export const getMembershipMember = createAsyncThunk<
  MembershipViewSource,
  string,
  { rejectValue: string }
>('api/membership-member/get', async (id, { rejectWithValue }) => {
  try {
    return await getMembershipMemberByIdApi(id)
  } catch (error) {
    return rejectWithValue(
      toErrorMessage(error, 'Failed to fetch membership member'),
    )
  }
})

export const createMembershipMember = createAsyncThunk<
  MembershipPageMember,
  CreateMembershipMemberInput,
  { rejectValue: string; state: RootState }
>(
  'api/membership-member/create',
  async (input, { rejectWithValue, getState }) => {
    try {
      const clinicId = resolveClinicId(getState())

      if (!clinicId) {
        return rejectWithValue(
          'No clinic is selected. Select a clinic before adding a member.',
        )
      }

      return await createMembershipMemberApi(
        mapMembershipMemberToCreateRequest({
          clinicId,
          patientId: input.patientId,
          membershipTierId: input.membershipTierId,
          memberSince: input.memberSince,
          expiryDate: input.expiryDate,
          paymentCycle: input.paymentCycle,
          recurringAmount: input.recurringAmount,
          autoPayMethod: input.autoPayMethod,
          notificationChannels: input.notificationChannels,
        }),
      )
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, 'Failed to create membership member'),
      )
    }
  },
)

export const updateMembershipMember = createAsyncThunk<
  MembershipViewSource,
  UpdateMembershipMemberInput,
  { rejectValue: string }
>('api/membership-member/update', async (input, { rejectWithValue }) => {
  try {
    return await updateMembershipMemberApi(
      mapMembershipMemberToUpdateRequest(input),
    )
  } catch (error) {
    return rejectWithValue(
      toErrorMessage(error, 'Failed to update membership member'),
    )
  }
})

export const updateMembershipMemberStatus = createAsyncThunk<
  MembershipViewSource,
  { id: string; status: MembershipMemberApiStatus; reason?: string },
  { rejectValue: string }
>(
  'api/membership-member/update/status',
  async ({ id, status, reason }, { rejectWithValue }) => {
    try {
      return await updateMembershipMemberStatusApi({
        membership_member_id: id,
        status,
        ...(reason ? { reason } : {}),
      })
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, 'Failed to update membership member status'),
      )
    }
  },
)

export const createMembershipTier = createAsyncThunk<
  MembershipTier,
  SaveMembershipTierPayload,
  { rejectValue: string; state: RootState }
>(
  'api/membership-tier/create',
  async ({ tier, perks }, { rejectWithValue, getState }) => {
    try {
      const clinicId = tier.clinicId || resolveClinicId(getState())

      if (!clinicId) {
        return rejectWithValue(
          'No clinic is selected. Select a clinic before adding a membership tier.',
        )
      }

      const payload = mapMembershipTierToCreateRequest(tier, perks, clinicId)
      return await createMembershipTierApi(payload)
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, 'Failed to create membership tier'),
      )
    }
  },
)

export const updateMembershipTier = createAsyncThunk<
  MembershipTier,
  SaveMembershipTierPayload,
  { rejectValue: string }
>(
  'api/membership-tier/update',
  async ({ tier, perks }, { rejectWithValue }) => {
    try {
      const payload = mapMembershipTierToUpdateRequest(tier, perks)
      return await updateMembershipTierApi(payload)
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, 'Failed to update membership tier'),
      )
    }
  },
)

export const updateMembershipTierStatus = createAsyncThunk<
  MembershipTier,
  { id: string; enabled: boolean },
  { rejectValue: string }
>(
  'api/membership-tier/update/status',
  async ({ id, enabled }, { rejectWithValue }) => {
    try {
      return await updateMembershipTierStatusApi({
        membership_tier_id: id,
        enabled,
      })
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, 'Failed to update membership tier status'),
      )
    }
  },
)

export const removeMembershipTier = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/membership-tier/remove', async (id, { rejectWithValue }) => {
  try {
    await deleteMembershipTierApi(id)
    return id
  } catch (error) {
    return rejectWithValue(
      toErrorMessage(error, 'Failed to delete membership tier'),
    )
  }
})

export const getMembershipMembers = createAsyncThunk<
  MembershipMemberListResponse,
  MembershipMemberQueryFilters | void,
  { rejectValue: string; state: RootState }
>(
  'api/membership-member/get/all',
  async (queryFilters, { rejectWithValue, getState }) => {
    try {
      const clinicId = resolveClinicId(getState())
      const filters: MembershipMemberQueryFilters = {
        ...(queryFilters || {}),
      }

      if (clinicId) {
        filters.clinic_id = clinicId
      }

      return await getMembershipMembersApi(
        Object.keys(filters).length > 0 ? filters : undefined,
      )
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, 'Failed to fetch membership members'),
      )
    }
  },
)

export const getMembershipMemberKpis = createAsyncThunk<
  MembershipKpiItem[],
  { clinic_id?: string } | void,
  { rejectValue: string; state: RootState }
>(
  'api/membership-member/get/kpis',
  async (queryFilters, { rejectWithValue, getState }) => {
    try {
      const clinicId = queryFilters?.clinic_id || resolveClinicId(getState())

      return await getMembershipMemberKpisApi(
        clinicId ? { clinic_id: clinicId } : undefined,
      )
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, 'Failed to fetch membership KPIs'),
      )
    }
  },
)
